import React, { useState } from "react";

import { LoadingButton } from "@mui/lab";
import { Box, Button, Modal, Stack, Typography } from "@mui/material";

import { toast } from "react-toastify";

import { useAllDeleteReferenceMeasurementsMutation } from "api/virtual-tour/mutations/useDeleteAllReferenceMeasurementsMutation";
import useUnityViewer from "hooks/useUnityViewer";

type Props = {
  referenceMeasurements?: VirtualTourReferenceMeasurement[];
  isPublic: boolean;
  virtualTourID: string;
};

function ExistingReferenceMeasurements({
  referenceMeasurements,
  isPublic,
  virtualTourID,
}: Props) {
  const [
    isStartReferenceMeasurementsModalOpen,
    setIsStartReferenceMeasurementsModalOpen,
  ] = useState(false);

  const { unityViewerContext } = useUnityViewer();

  const {
    mutate: deleteAllReferenceMeasurementMutation,
    isPending: deleteAllReferenceMeasurementsIsPending,
  } = useAllDeleteReferenceMeasurementsMutation();

  const handleStartReferenceMeasurementsOver = () => {
    deleteAllReferenceMeasurementMutation(
      {
        tourId: virtualTourID,
      },
      {
        onSuccess: () => {
          // toast.success("Reference Measurements deleted successfully");
          setIsStartReferenceMeasurementsModalOpen(false);
          // window.location.reload();
          if (!unityViewerContext) {
            // if couldn't find any unity context to clear the existing messages, reload
            window.location.reload();
          } else {
            // else just clear the messages from the unity viewer
            unityViewerContext.sendMessage("Ruler", "DeleteAllMeasurements");
            toast.success("Reference Measurements deleted successfully");
          }
        },
        onError: () => {
          toast.error("Failed to delete reference measurements");
        },
      }
    );
  };

  return (
    <Box
      sx={(theme) => ({
        [theme.breakpoints.down("md")]: { width: "100%" },
        position: "absolute",
        bottom: "-5%",
        transform: "translateY(50%)",
        width: "100%",
      })}
    >
      <Stack
        spacing={0.5}
        sx={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: ".9rem",
          }}
        >
          {referenceMeasurements?.length || "No"} reference
          {referenceMeasurements?.length === 0
            ? " measurements marked yet"
            : referenceMeasurements?.length === 1
              ? " measurement marked"
              : " measurements marked"}
        </Typography>
        {!isPublic &&
          referenceMeasurements &&
          referenceMeasurements?.length > 0 && (
            <Button
              variant="text"
              color="error"
              onClick={() => setIsStartReferenceMeasurementsModalOpen(true)}
            >
              <Typography fontWeight={"bold"}>Start Over?</Typography>
            </Button>
          )}
      </Stack>
      <Modal
        open={isStartReferenceMeasurementsModalOpen}
        onClose={() => setIsStartReferenceMeasurementsModalOpen(false)}
        sx={{
          display: "grid",
          placeItems: "center",
        }}
      >
        <Stack
          sx={{
            backgroundColor: "background.paper",
            padding: 2,
            borderRadius: 2,
            maxWidth: 400,
          }}
          gap={2}
        >
          <Stack gap={1}>
            <Typography fontSize={"1.25rem"} fontWeight={"bold"}>
              Are you sure you want to start over?
            </Typography>
            <Typography color={"error"} fontSize={"1rem"}>
              Please note that this will delete all existing reference
              measurements.
            </Typography>
          </Stack>
          <Stack direction={"row"} gap={1} justifyContent={"flex-end"}>
            <Button
              variant="outlined"
              color="info"
              onClick={() => setIsStartReferenceMeasurementsModalOpen(false)}
            >
              <Typography fontWeight={"bold"}>Cancel</Typography>
            </Button>
            <LoadingButton
              variant="contained"
              color="error"
              onClick={handleStartReferenceMeasurementsOver}
              loading={deleteAllReferenceMeasurementsIsPending}
            >
              <Typography fontWeight={"bold"}>Start Over</Typography>
            </LoadingButton>
          </Stack>
        </Stack>
      </Modal>
    </Box>
  );
}

export default ExistingReferenceMeasurements;
